import type { ListMemoryThreadMessagesResponse } from '@mastra/client-js';
import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import mastraClient from '../lib/mastraClient';
import type { AgentNames } from '../types';

const THREADS_QUERY_KEY = ['AI', 'THREAD'];

/**
 * Get a specific thread's messages
 * @param agentName - The agent ID
 * @param threadId - The thread ID
 * @returns The thread messages
 */
export const useThreadMessages = ({
  agentName,
  threadId,
  options,
}: {
  agentName: AgentNames;
  threadId: string | undefined;
  options?: Partial<UseQueryOptions<ListMemoryThreadMessagesResponse>>;
}) => {
  const isEnabled = Boolean(threadId) && (options?.enabled ?? true);

  return useQuery({
    queryKey: [...THREADS_QUERY_KEY, agentName, threadId],
    queryFn: async () => {
      if (!threadId) throw new Error('Thread ID is required');
      const client = await mastraClient();
      const thread = await client.getMemoryThread({ threadId, agentId: agentName });
      const messages = await thread.listMessages();
      return messages;
    },
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: isEnabled,
    ...options,
  });
};
